<template>
  <div>
    <div class="row">
      <div class="col-lg-12">
        <div class="card card-default">
          <div class="card-header">
            <h3>Scheduled Tasks</h3>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-12">
                <div class="table-responsive table-bordered">
                  <table class="table">
                    <thead>
                      <tr>
                        <th>Task Name</th>
                        <th>Schedule</th>
                        <th>Enabled</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="task in scheduledTasks" :key="task.scheduledTaskNumber">
                        <td>{{ task.taskName }}</td>
                        <td>{{ formatScheduleDisplay(task.schedule) }}</td>
                        <td>
                          <div class="form-check form-switch">
                            <input
                              class="form-check-input"
                              type="checkbox"
                              :checked="task.enabled"
                              @change="toggleEnabled(task)"
                            />
                          </div>
                        </td>
                        <td class="text-center">
                          <button
                            class="btn btn-info btn-sm m-1"
                            type="button"
                            @click="openEditModal(task)"
                          >
                            <i class="fa fa-edit"></i> Edit
                          </button>
                          <button
                            class="btn btn-danger btn-sm m-1"
                            type="button"
                            @click="confirmDelete(task)"
                          >
                            <i class="fa fa-trash"></i> Delete
                          </button>
                        </td>
                      </tr>
                      <tr v-if="scheduledTasks.length === 0">
                        <td colspan="4" class="text-center">
                          No scheduled tasks found. Go to 
                          <router-link :to="{ name: 'runtask' }">Run Task</router-link> 
                          to schedule a new task.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <modal
      :title="'Edit Schedule: ' + selectedTask.taskName"
      ref="editScheduleModal"
      size="modal-lg"
      save-text="Save"
      @save="saveSchedule()"
      dismiss-text="Cancel"
    >
      <div v-if="selectedTask.taskName">
        <h5>Schedule</h5>
        <ScheduleEditor v-model="editSchedule" />

        <hr />

        <h5>Parameters</h5>
        <div v-if="taskParameters.length > 0">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="param in taskParameters" :key="param.name">
                <td>{{ param.description || param.name }}</td>
                <td>
                  <input
                    type="checkbox"
                    v-model="param.value"
                    v-if="param.type === 'BOOLEAN'"
                  />
                  <input
                    type="text"
                    v-model="param.value"
                    v-else-if="param.type === 'STRING'"
                    class="form-control"
                  />
                  <input
                    type="number"
                    v-model="param.value"
                    v-else-if="param.type === 'INT'"
                    class="form-control"
                  />
                  <input
                    type="number"
                    v-model="param.value"
                    v-else-if="param.type === 'DOUBLE'"
                    class="form-control"
                  />
                  <DateInput
                    v-model="param.value"
                    v-else-if="param.type === 'DATE'"
                  />
                  <span v-else>{{ param.value }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="text-muted">No parameters configured for this task.</p>

        <hr />

        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            v-model="selectedTask.enabled"
            id="enabledCheck"
          />
          <label class="form-check-label" for="enabledCheck">
            Enabled
          </label>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import Modal from "@/components/GeneralComponents/Modal";
import ScheduleEditor from "@/components/GeneralComponents/ScheduleEditor";
import DateInput from "@/components/GeneralComponents/InputControls/DateInput";

export default {
  components: {
    Modal,
    ScheduleEditor,
    DateInput
  },
  data() {
    return {
      scheduledTasks: [],
      selectedTask: {},
      editSchedule: null,
      taskParameters: [],
      taskDefinitions: []
    };
  },
  mounted() {
    this.$root.$emit("SET_PAGE_TITLE", "Scheduled Tasks");
    this.loadScheduledTasks();
    this.loadTaskDefinitions();
  },
  methods: {
    loadScheduledTasks() {
      this.$http.get(this.$config.aimsAPIv3 + "schedule/tasks").then(
        response => {
          this.scheduledTasks = response.data;
        },
        error => {
          console.error(error);
          this.showError("Error Fetching Scheduled Tasks", error);
        }
      );
    },
    loadTaskDefinitions() {
      this.$http.get(this.$config.aimsAPI + "tasks/definitions").then(
        response => {
          this.taskDefinitions = response.data;
        },
        error => {
          console.error(error);
        }
      );
    },
    formatScheduleDisplay(scheduleJson) {
      if (!scheduleJson) return "Not configured";
      
      try {
        const schedule = typeof scheduleJson === "string" 
          ? JSON.parse(scheduleJson) 
          : scheduleJson;
        return this.formatSchedule(schedule);
      } catch (e) {
        return "Invalid schedule";
      }
    },
    formatSchedule(schedule) {
      if (!schedule) return "Not configured";

      // Handle interval-based schedules
      if (schedule.minute && typeof schedule.minute === "object" && schedule.minute.interval) {
        const interval = schedule.minute.interval;
        const offset = schedule.minute.offset || 0;
        if (interval === 60) {
          return `Every hour at :${offset.toString().padStart(2, "0")}`;
        }
        return `Every ${interval} minutes (starting at :${offset.toString().padStart(2, "0")})`;
      }

      const parts = [];

      // Build time string
      let timeStr = "";
      if (schedule.hour !== "any" && schedule.minute !== "any") {
        const hour = schedule.hour.toString().padStart(2, "0");
        const minute = schedule.minute.toString().padStart(2, "0");
        timeStr = `at ${hour}:${minute}`;
      } else if (schedule.hour !== "any") {
        timeStr = `at ${schedule.hour.toString().padStart(2, "0")}:00`;
      }

      // Build day string
      if (schedule.dayOfWeek === "weekDays") {
        parts.push("Weekdays");
      } else if (schedule.dayOfWeek === "weekendDays " || schedule.dayOfWeek === "weekendDays") {
        parts.push("Weekends");
      } else if (schedule.dayOfWeek !== "any") {
        const dayName = schedule.dayOfWeek.charAt(0) + schedule.dayOfWeek.slice(1).toLowerCase();
        parts.push(`Every ${dayName}`);
      } else if (schedule.dayOfMonth === "first") {
        parts.push("First day of month");
      } else if (schedule.dayOfMonth === "last") {
        parts.push("Last day of month");
      } else if (schedule.dayOfMonth !== "any") {
        parts.push(`Day ${schedule.dayOfMonth} of month`);
      } else {
        parts.push("Daily");
      }

      if (timeStr) {
        parts.push(timeStr);
      }

      return parts.join(" ");
    },
    toggleEnabled(task) {
      const updatedTask = { ...task, enabled: !task.enabled };
      this.$http.put(this.$config.aimsAPIv3 + "schedule/tasks", updatedTask).then(
        () => {
          this.showSuccess(updatedTask.enabled ? "Task enabled" : "Task disabled");
          this.loadScheduledTasks();
        },
        error => {
          console.error(error);
          this.showError("Error updating task", error);
        }
      );
    },
    openEditModal(task) {
      this.selectedTask = { ...task };
      
      // Parse the schedule JSON and not Stringify
      try {
        this.editSchedule = typeof task.schedule === "string" 
          ? JSON.parse(task.schedule) 
          : task.schedule;
      } catch (e) {
        this.editSchedule = null;
      }
      this.taskParameters = [];
      if (task.jobSetup) {
        try {
          const jobSetup = typeof task.jobSetup === "string" 
            ? JSON.parse(task.jobSetup) 
            : task.jobSetup;
          
          if (jobSetup.parameters) {
            this.$http.get(
              this.$config.aimsAPI + "tasks/definitions/" + task.taskName + "/params/input"
            ).then(
              response => {
                const paramDefs = response.data;
                this.taskParameters = paramDefs
                  .filter(p => p.type !== "CONTENT")
                  .map(paramDef => {
                    const storedParam = jobSetup.parameters[paramDef.name];
                    return {
                      ...paramDef,
                      value: storedParam !== undefined ? storedParam : paramDef.defaultValue || ""
                    };
                  });
              },
              error => {
                console.error(error);
                this.taskParameters = Object.entries(jobSetup.parameters).map(([name, value]) => ({
                  name,
                  value,
                  type: "STRING"
                }));
              }
            );
          }
        } catch (e) {
          console.error("Error parsing jobSetup:", e);
        }
      }

      this.$refs.editScheduleModal.open();
    },
    saveSchedule() {
      this.$refs.editScheduleModal.isLoading();

      const parameters = {};
      this.taskParameters.forEach(param => {
        if (param.value !== undefined && param.value !== "") {
          parameters[param.name] = String(param.value);
        }
      });

      const updatedTask = {
        ...this.selectedTask,
        schedule: JSON.stringify(this.editSchedule),
        jobSetup: JSON.stringify({ parameters })
      };

      this.$http.put(this.$config.aimsAPIv3 + "schedule/tasks", updatedTask).then(
        () => {
          this.$refs.editScheduleModal.close();
          this.showSuccess("Schedule updated successfully");
          this.loadScheduledTasks();
        },
        error => {
          console.error(error);
          this.showError("Error saving schedule", error);
        }
      );
    },
    confirmDelete(task) {
      this.$swal({
        title: "Are you sure?",
        text: `Delete scheduled task "${task.taskName}"? This cannot be undone.`,
        type: "warning",
        showCancelButton: true,
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it"
      }).then(result => {
        if (result.value) {
          this.deleteTask(task);
        }
      });
    },
    deleteTask(task) {
      this.$http.delete(this.$config.aimsAPIv3 + "schedule/tasks/" + task.scheduledTaskNumber)
        .then(() => {
          this.showSuccess("Scheduled task deleted");
          this.loadScheduledTasks();
        }, error => {
          console.error(error);
          this.showError("Error deleting scheduled task", error);
        });
    }
  }
};
</script>
